import { DateTime } from 'luxon';
import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { SeverityAlert } from '../../../../../appHelpers/appCommonSC';

// Helper function to format value with unit, handling undefined units
const formatValueWithUnit = (value, unit) => {
   const cleanUnit = unit || '';
   return `${value}${cleanUnit ? ' ' + cleanUnit : ''}`;
};

export const TableFC = ({metadata, dataTable, dataConfig, filterLevel, alertConfigs, tableSwitch}) => {
   let chartConfig = dataTable[0].chartConfig;
   let columnConfig = dataConfig[0].dataLabels;
   let columns = columnConfig.map(col => (col.pgColumn));
   let columnWidth = 125;
   let displayDataTable = dataTable[0].dataTable;
   let data = dataTable[0].dataTable;

   const [filterConfig, setFilteredConfig] = useState(dataTable[0].filterConfig)
   const [filteredDataLabel, setFilteredDataLabel] = useState(dataConfig[0].dataLabels)
   const { dataLabels } = dataConfig[0];
   const [tableRows, setTableRows] = useState(dataTable[0].dataTable);
   const [unitToggleStates, setUnitToggleStates] = useState({});
   
   // Field conversion type mapping - determines what conversions are available
   const fieldConversionTypes = {
      // Temperature fields
      'temp_probe_0_value': 'temperature',
      'temp_probe_1_value': 'temperature', 
      'room_temp_value': 'temperature',
      'water_temp_value': 'temperature',
      'tech_room_temp_value': 'temperature',
      'case_temp_value': 'temperature',
      // Flow rate fields
      'water_flow_value': 'flow',
      // Helium level fields  
      'he_level_value': 'helium',
      'he_level_1_value': 'helium',
      'he_level_2_value': 'helium',
   };

   // Dynamic unit conversion mapping based on incoming units
   const getUnitConversion = (fieldName, currentUnit, tankVolume = null) => {
      const conversionType = fieldConversionTypes[fieldName];
      if (!conversionType) return null;

      switch (conversionType) {
         case 'temperature':
            if (currentUnit?.includes('°F') || currentUnit?.includes('F')) {
               return { primary: currentUnit, secondary: '°C', type: 'temperature' };
            } else if (currentUnit?.includes('°C') || currentUnit?.includes('C')) {
               return { primary: currentUnit, secondary: '°F', type: 'temperature' };
            }
            return { primary: '°F', secondary: '°C', type: 'temperature' }; // default
         case 'flow':
            if (currentUnit?.includes('GPM')) {
               return { primary: currentUnit, secondary: 'LPM', type: 'flow' };
            } else if (currentUnit?.includes('LPM') || currentUnit?.includes('L/MIN')) {
               return { primary: currentUnit, secondary: 'GPM', type: 'flow' };
            }
            return { primary: 'GPM', secondary: 'LPM', type: 'flow' }; // default
         case 'helium':
            // Only allow conversion if we have tank volume and original unit is specifically LTRS
            if (currentUnit?.includes('%')) {
               // If the unit is already percent, don't allow conversion back to liters
               return null;
            }
            // Only allow conversion to percent if original unit is exactly 'LTRS' and we have tank volume
            // This matches the original condition: unit === 'LTRS' && metadata.he_tank_volume
            if (currentUnit === 'LTRS' && tankVolume) {
               return { primary: '%', secondary: currentUnit, type: 'helium' };
            }
            return null;
         default:
            return null;
      }
   };

   // Unit conversion functions
   const convertValue = (value, conversionType, fromUnit, toUnit, tankVolume = null) => {
      if (!value || isNaN(value)) return value;

      switch (conversionType) {
         case 'temperature':
            // Determine actual conversion direction based on units
            if (fromUnit?.includes('°F') || fromUnit?.includes('F')) {
               return ((parseFloat(value) - 32) * 5/9).toFixed(1); // F to C
            } else {
               return (parseFloat(value) * 9/5 + 32).toFixed(1);  // C to F
            }
         case 'flow':
            // Explicitly handle GPM <-> LPM conversion
            if (fromUnit?.includes('GPM') && toUnit?.includes('LPM')) {
               return (parseFloat(value) * 3.78541).toFixed(2); // GPM to LPM
            } else if (fromUnit?.includes('LPM') && toUnit?.includes('GPM')) {
               return (parseFloat(value) / 3.78541).toFixed(2); // LPM to GPM
            }
         case 'volume':
            if (fromUnit?.includes('GAL') && toUnit?.includes('LTRS')) {
               return (parseFloat(value) * 3.78541).toFixed(2); // GAL to LTRS
            } else if (fromUnit?.includes('LTRS') && toUnit?.includes('GAL')) {
               return (parseFloat(value) / 3.78541).toFixed(2); // LTRS to GAL
            }
            return value;
         case 'helium':
            if (!tankVolume) return value;
            // Ensure both value and tankVolume are in the same units before conversion
            const numericValue = parseFloat(value);
            const numericTankVolume = parseFloat(tankVolume);

            if (fromUnit === 'LTRS' && toUnit?.includes('%')) {
               return (numericValue / numericTankVolume * 100).toFixed(2); // LTRS to %
            } else if (fromUnit?.includes('%') && toUnit === 'LTRS') {
               return ((numericValue * numericTankVolume) / 100).toFixed(2); // % to LTRS
            }
            return value;
         default:
            return value;
      }
   };

   // Get display unit based on toggle state and dynamic conversion
   const getDisplayUnit = (fieldName, originalUnit) => {
      const conversion = getUnitConversion(fieldName, originalUnit, metadata.he_tank_volume);
      if (!conversion) return originalUnit || '';
      
      const isToggled = !!unitToggleStates[fieldName];
      const unit = isToggled ? conversion.secondary : conversion.primary;
      
      
      return unit || '';
   };

   // Get decimal places from the original value
   const getDecimalPlaces = (value) => {
      const str = String(value);
      if (str.includes('.')) {
         return str.split('.')[1].length;
      }
      return 0;
   };

   // Get display value based on toggle state and dynamic conversion
   const getDisplayValue = (fieldName, value) => {
      const originalUnit = chartConfig?.filter(config => config.pgcolumn === fieldName)?.[0]?.unit;
      const conversion = getUnitConversion(fieldName, originalUnit, metadata.he_tank_volume);

      if (!conversion) return value;

      const isToggled = !!unitToggleStates[fieldName];
      const decimals = getDecimalPlaces(value);

      // Determine which unit to display
      const displayUnit = isToggled ? conversion.secondary : conversion.primary;

      // Handle different conversion types
      if (conversion.type === 'helium') {
         // For helium: primary is %, secondary is LTRS (original unit)
         // Original data is always in LTRS, so:
         // If not toggled, show primary (%) - convert LTRS to %
         // If toggled, show secondary (LTRS) - show original value

         if (!isToggled) {
            // Show primary (%) - convert from LTRS to %
            const tankVolume = metadata.he_tank_volume;
            return (parseFloat(value) / parseFloat(tankVolume) * 100).toFixed(decimals);
         } else {
            // Show secondary (LTRS) - return original value
            return value;
         }
      } else if (conversion.type === 'temperature' || conversion.type === 'flow') {
         // For temperature and flow: handle toggle state properly
         if (!isToggled) {
            // Show primary (original unit) - return original value
            return value;
         } else {
            // Show secondary (converted unit) - need to convert with same decimals
            if (conversion.type === 'temperature') {
               if (originalUnit?.includes('°F') || originalUnit?.includes('F')) {
                  return ((parseFloat(value) - 32) * 5/9).toFixed(decimals);
               } else {
                  return (parseFloat(value) * 9/5 + 32).toFixed(decimals);
               }
            } else if (conversion.type === 'flow') {
               if (originalUnit?.includes('GPM') && displayUnit?.includes('LPM')) {
                  return (parseFloat(value) * 3.78541).toFixed(decimals);
               } else if (originalUnit?.includes('LPM') && displayUnit?.includes('GPM')) {
                  return (parseFloat(value) / 3.78541).toFixed(decimals);
               }
            }
         }
      }

      return value;
   };

   // Toggle unit for a specific field
   const toggleUnit = (fieldName) => {
      setUnitToggleStates(prev => ({
         ...prev,
         [fieldName]: !prev[fieldName]
      }));
   };


   const rowHeight = 40;
   const visibleRowCount = 25;
   const [scrollTop, setScrollTop] = useState(0);
   const containerRef = useRef(null);
   const totalHeight = data?.length * rowHeight;
   const startRow = Math.floor(scrollTop / rowHeight);
   const endRow = startRow + visibleRowCount;
   let visibleData = tableRows.slice(startRow, endRow);

   const removeUnusedColumns = () => {
      const dataTableKeys = Object.keys(displayDataTable[0]);
      setFilteredDataLabel(dataLabels.filter(label => dataTableKeys.includes(label.pgColumn)));
   }

   const filterColumns = () => {
      let dataTableKeys = Object.keys(displayDataTable[0]);

      const sttTableKeys = ['he_level_value', 'he_pressure_value', 'he_boil_off_value']
      const eduTableKeys = ['temp_probe_1_value', 'temp_probe_0_value', 'room_humidity_value', 'room_temp_value', 'comp_vib_status'];
      const rmmuTableKeys = ['avgpwr', 'minpwr', 'maxpwr', 'minpr', 'maxpr', 'lhe1', 'lhe2', 'dps', 'talm', 'palm', 'cres']
      const rmmuShortTableKeys = [ 'avgpwr', 'minpwr', 'maxpwr', 'minpr', 'avgpr', 'maxpr', 'lhe1', 'lhe2', 'dps', 'talm', 'palm', 'cres', 'avgabs_value', 'cerr_state', 'compressorreset_state', 'chd_value', 'cpr_value']

      let filterLevelArray = [1, 2, 3 , 4];

      if (tableSwitch === 'EDU') {
         dataTableKeys = dataTableKeys.filter(item => eduTableKeys.includes(item));
      } else if (tableSwitch === 'MAGNET') {
         dataTableKeys = dataTableKeys.filter(item => !eduTableKeys.includes(item) && !rmmuTableKeys.includes(item) && !rmmuShortTableKeys.includes(item) && !sttTableKeys.includes(item));
      } else if (tableSwitch === 'RMMU_HISTORY') {
         dataTableKeys = dataTableKeys.filter(item => rmmuTableKeys.includes(item));
      } else if (tableSwitch === 'RMMU_SHORT' || tableSwitch === 'RMMU_LONG' ) {
         dataTableKeys = dataTableKeys.filter(item => rmmuShortTableKeys.includes(item));
      } else if (tableSwitch === 'STT') {
         dataTableKeys = dataTableKeys.filter(item => sttTableKeys.includes(item));
      }

      if (filterLevel === 1) {
         filterLevelArray = [1];
      } else if (filterLevel === 2) {
         filterLevelArray = [1,2]
      } else if (filterLevel === 3) {
         filterLevelArray = [1, 2, 3]
      }

      if (filterLevel !== 4) {
         let tempConfig = filterConfig?.filter(config => filterLevelArray.includes(config[2]) && dataTableKeys.includes(config[0]));
         let pgColumns = tempConfig?.map(config => config[0]);

         if (pgColumns?.length > 0) {
            setFilteredDataLabel(dataLabels.filter(label => pgColumns.includes(label.pgColumn)));
        } else {
            setFilteredDataLabel(dataLabels.filter(label => dataTableKeys.includes(label.pgColumn)));
        }
      } else {
         setFilteredDataLabel(dataLabels.filter(label => dataTableKeys.includes(label.pgColumn)));
      }
   }

   const filterTableRows = () => {
      if(tableSwitch === 'COMBINED') {
         setTableRows(dataTable[0].dataTable);
      } else if (tableSwitch === 'EDU'){
         setTableRows(tableRows.filter(row => row.table_name === 'edu'))
      } else if (tableSwitch === 'MAGNET'){
         setTableRows(tableRows.filter(row => row.table_name === 'philips_mri'))
      } else if (tableSwitch === 'STT'){
         setTableRows(tableRows.filter(row => row.table_name === 'stt_magnet'))
      } else if (tableSwitch === 'RMMU_HISTORY'){
         setTableRows(tableRows.filter(row => row.table_name === 'rmmu_history'))
      } else if (tableSwitch === 'RMMU_SHORT'){
         setTableRows(tableRows.filter(row => row.table_name === 'rmmu_short'))
      } else if (tableSwitch === 'RMMU_LONG'){
         setTableRows(tableRows.filter(row => row.table_name === 'rmmu_long'))
      }
   }

   const handleScroll = (e) => {
      setScrollTop(e.currentTarget.scrollTop);
   };

   useEffect(() => {
      removeUnusedColumns();
   }, [dataTable]);

   useEffect(() => {
      filterColumns();    
   }, [filterLevel]);

   useEffect(() => {
      filterTableRows();
   }, [tableSwitch]);
   
   const calculatedMinWidth = 175 + (filteredDataLabel.length * 120);

   return (
      <TableSC>
         <Table style={{ minWidth: `${calculatedMinWidth}px` }}>

         {/* Column Headers */}
         <TableHeader>
            <CellHeaderAlign key={'datetimeKey'} columnWidth = {175}>
            {'DateTime'}
            </CellHeaderAlign>
            {filteredDataLabel.map((col, colIndex) => {
               const originalUnit = chartConfig?.filter(config => config.pgcolumn === col.pgColumn)?.[0]?.unit;
               const hasConversion = getUnitConversion(col.pgColumn, originalUnit, metadata.he_tank_volume);
               const currentUnit = hasConversion 
                  ? getDisplayUnit(col.pgColumn, originalUnit)
                  : originalUnit;
               
               return (
                  <CellHeader key={colIndex} >
                     <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px'}}>
                        <span>{col.uiLabel}</span>
                        {hasConversion && (
                           <UnitToggleButton 
                              onClick={() => toggleUnit(col.pgColumn)}
                              isToggled={unitToggleStates[col.pgColumn]}
                           >
                              {currentUnit || hasConversion.primary}
                           </UnitToggleButton>
                        )}
                     </div>
                  </CellHeader>
               )
            })}
         </TableHeader>

         {/* Table Body */}
         <div
            ref={containerRef}
            onScroll={handleScroll}
            style={{
               height: `58vh`,
               overflowY: 'scroll',
               width: '100%',
            }}
         >
            <div
               style={{
               height: `${totalHeight}px`,
               position: 'relative',
               width: '100%',
               }}
            >
               {visibleData.map((row, rowIndex) => (
               <TableRow
                  key={rowIndex}
                  isEven={(startRow + rowIndex) % 2 === 0}
                  style={{
                     position: 'absolute',
                     top: `${(startRow + rowIndex) * rowHeight}px`,
                     height: `${rowHeight}px`,
                     width: '100%',
                  }}
               >
                  <CellLeftAlign 
                     key={`datetime-${rowIndex}`} 
                     columnWidth = {175}
                     title={row.host_datetime && tableSwitch !== 'EDU' ? 
                        `Host Datetime: ${DateTime.fromISO(row.host_datetime).toFormat('ff')}` 
                        : undefined}
                  >
                     {DateTime.fromISO(row.capture_datetime).toFormat(
                        'ff'
                  )}
                  </CellLeftAlign>
                  {filteredDataLabel.map((col, colIndex) => (
                     col.pgColumn === 'comp_vib_status' ?
                        <Cell key={colIndex} >
                           {row[col.pgColumn] === null ? '-' : row[col.pgColumn] === 1 ? <SeverityAlert>ON</SeverityAlert> : <SeverityAlert severity='high'>OFF</SeverityAlert>} 
                        </Cell>
                     : col.pgColumn === 'temp_probe_0_value' ||  col.pgColumn === 'temp_probe_1_value' ?
                        <Cell key={colIndex} >
                            {row[col.pgColumn] <= 0 ? '-' : formatValueWithUnit(getDisplayValue(col.pgColumn, row[col.pgColumn]), getDisplayUnit(col.pgColumn, chartConfig?.filter(configUnit => configUnit.pgcolumn === col.pgColumn)?.[0]?.unit))}
                        </Cell>
                     : (col.pgColumn === 'he_level_1_value' || col.pgColumn === 'he_level_value') && (chartConfig?.filter(config => config.pgcolumn === col.pgColumn)[0].unit === 'LTRS' && metadata.he_tank_volume) ?
                        <Cell key={colIndex} >
                           {displayHeliumSeverityAlert(alertConfigs.filter(config => config.field_name === col.pgColumn && config.user_id !== 'default'), row[col.pgColumn], chartConfig, metadata.he_tank_volume, col.pgColumn, getDisplayValue, getDisplayUnit, unitToggleStates)}
                        </Cell>
                     : row[col.pgColumn] || row[col.pgColumn] === 0 ?
                        <Cell key={colIndex} >
                           {alertConfigs.filter(config => config.field_name === col.pgColumn && config.user_id !== 'default').length > 0 ?
                              displaySeverityAlert(alertConfigs.filter(config => config.field_name === col.pgColumn && config.user_id !== 'default'), row[col.pgColumn], chartConfig, col.pgColumn, getDisplayValue, getDisplayUnit)
                           :
                           formatValueWithUnit(getDisplayValue(col.pgColumn, row[col.pgColumn]), getDisplayUnit(col.pgColumn, chartConfig?.filter(config => config.pgcolumn === col.pgColumn)?.[0]?.unit))
                           }
                        </Cell>
                     : <Cell key={colIndex} >
                           -
                        </Cell>
                  ))}
               </TableRow>
               ))}
            </div>
         </div>
         </Table>
      </TableSC>
   )
};

const displaySeverityAlert = (configs, rowValue, chartConfig, fieldName = null, getDisplayValue = null, getDisplayUnit = null) => {
  let mediumAlert = null;
  let unit = null;
  
  // Use converted values if conversion functions are provided
  const displayValue = getDisplayValue && fieldName ? getDisplayValue(fieldName, rowValue) : rowValue;
  const displayUnitValue = getDisplayUnit && fieldName 
    ? getDisplayUnit(fieldName, chartConfig?.filter(configUnit => configUnit.pgcolumn === fieldName)?.[0]?.unit)
    : chartConfig?.filter(configUnit => configUnit.pgcolumn === configs[0]?.field_name)?.[0]?.unit;
 
  // Use original rowValue for threshold comparison since thresholds are based on original units
  for (let config of configs) {
    if ((config.operator === 'less_than' && parseFloat(rowValue) < parseFloat(config.threshold)) ||
       (config.operator === 'greater_than' && parseFloat(rowValue) > parseFloat(config.threshold)) ||
       (config.operator === 'equals' && parseFloat(rowValue) === parseFloat(config.threshold))) {
      if (config.severity === 'high') {
        return <SeverityAlert severity="high">{displayValue} {displayUnitValue}</SeverityAlert>;
      } else if (config.severity === 'medium') {
        mediumAlert = <SeverityAlert severity="medium">{displayValue} {displayUnitValue}</SeverityAlert>;
      }
    }
    unit = displayUnitValue || chartConfig?.filter(configUnit => configUnit.pgcolumn === config.field_name)?.[0]?.unit;
  }

  if (mediumAlert) {
   return mediumAlert;
  } else {
   return <SeverityAlert>{formatValueWithUnit(displayValue, unit)}</SeverityAlert>;
  }
};

const displayHeliumSeverityAlert = (configs, rowValue, chartConfig, tankVolume, fieldName = null, getDisplayValue = null, getDisplayUnit = null, unitToggleStates = {}) => {
   let mediumAlert = null;
   let unit = null;

   // Check if we should use unit conversion
   const isToggled = fieldName && unitToggleStates[fieldName];
   let displayValue, displayUnitValue;
   
   if (getDisplayValue && getDisplayUnit && fieldName) {
      // Use conversion functions if available
      displayValue = getDisplayValue(fieldName, rowValue);
      displayUnitValue = getDisplayUnit(fieldName, chartConfig?.filter(configUnit => configUnit.pgcolumn === fieldName)?.[0]?.unit);
   } else {
      // Default to percentage display (legacy behavior)
      displayValue = (parseFloat(rowValue) / parseFloat(tankVolume) * 100).toFixed(2);
      displayUnitValue = '%';
   }

   for (let config of configs) {
     // Always use percentage for threshold comparison, regardless of display unit
     // This ensures alert colors remain consistent when toggling between LTRS and %
     const comparisonValue = (parseFloat(rowValue) / parseFloat(tankVolume) * 100);
        
     if ((config.operator === 'less_than' && comparisonValue < parseFloat(config.threshold)) ||
        (config.operator === 'greater_than' && comparisonValue > parseFloat(config.threshold)) ||
        (config.operator === 'equals' && comparisonValue === parseFloat(config.threshold))) {
       if (config.severity === 'high') {
         return <SeverityAlert severity="high">{displayValue} {displayUnitValue}</SeverityAlert>;
       } else if (config.severity === 'medium') {
         mediumAlert = <SeverityAlert severity="medium">{displayValue} {displayUnitValue}</SeverityAlert>;
       }
     }
     unit = displayUnitValue || chartConfig?.filter(configUnit => configUnit.pgcolumn === config.field_name)?.[0]?.unit;
   }
 
   if (mediumAlert) {
    return mediumAlert;
   } else {
    return <SeverityAlert>{formatValueWithUnit(displayValue, displayUnitValue)}</SeverityAlert>;
   }
 };

const TableSC  = styled.div`
   width: 100%;
   overflow-x: auto;
   border-radius: 12px;
   box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
   background: rgba(255, 255, 255, 0.7);
   backdrop-filter: blur(10px);
   -webkit-backdrop-filter: blur(10px);
   border: 1px solid rgba(255, 255, 255, 0.2);
`

const Table  = styled.div`
   width: 100%;
   overflow: hidden;
`

const TableHeader = styled.div`
   padding: 8px 17px 8px 0;
   display: flex;
   width: 100%;
   color: white;
   background: linear-gradient(135deg, rgba(0, 91, 148, 0.95) 0%, rgba(0, 70, 120, 0.95) 100%);
   font-weight: 600;
   font-size: 13px;
   border-bottom: 2px solid rgba(0, 91, 148, 0.3);
   border-radius: 12px 12px 0 0;
   box-sizing: border-box;
`;

const Cell = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;
   text-align: center;
   flex: 1;
   min-width: 100px;
   border-bottom: 1px solid rgba(0, 0, 0, 0.06);
   padding: 6px 8px;
   font-size: 13px;
   transition: background-color 0.15s ease;
`

const CellLeftAlign = styled.div`
   display: flex;
   align-items: center;
   flex-shrink: 0;
   width: 175px;
   border-bottom: 1px solid rgba(0, 0, 0, 0.06);
   padding: 6px 12px;
   font-size: 13px;
   font-weight: 500;
   color: #374151;
   transition: background-color 0.15s ease;
`

const CellHeader = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;
   text-align: center;
   flex: 1;
   min-width: 100px;
   padding: 6px 8px;
   font-size: 13px;
`

const CellHeaderAlign = styled.div`
   display: flex;
   align-items: center;
   flex-shrink: 0;
   width: 175px;
   padding: 6px 12px;
   font-size: 13px;
`

const TableRow = styled.div`
   display: flex;
   background-color: ${({ isEven }) => isEven ? 'rgba(249, 250, 251, 0.8)' : 'rgba(255, 255, 255, 0.9)'};
   transition: all 0.15s ease;

   &:hover {
      background-color: rgba(0, 91, 148, 0.06);
      box-shadow: inset 0 0 0 1px rgba(0, 91, 148, 0.1);
   }
`

const UnitToggleButton = styled.button`
   background: ${({ isToggled }) => isToggled
      ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'
      : 'linear-gradient(135deg, #6c757d 0%, #495057 100%)'};
   color: white;
   border: none;
   border-radius: 6px;
   padding: 3px 8px;
   font-size: 10px;
   font-weight: 600;
   cursor: pointer;
   transition: all 0.2s ease;
   min-width: 38px;
   box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
   text-transform: uppercase;
   letter-spacing: 0.3px;

   &:hover {
      transform: translateY(-1px);
      box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2);
      background: ${({ isToggled }) => isToggled
         ? 'linear-gradient(135deg, #218838 0%, #1ea97c 100%)'
         : 'linear-gradient(135deg, #5a6268 0%, #3d4246 100%)'};
   }

   &:active {
      transform: translateY(0);
   }

   &:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.3);
   }
`